/**
 * menuHelper.js - 菜单帮助工具
 * 根据路由路径从动态菜单数据（localStorage）中查找完整的面包屑路径
 */

/**
 * 递归搜索菜单树，找到匹配的菜单项及其路径
 * @param {Array} menus - 菜单数组
 * @param {String} routePath - 要查找的路由路径
 * @param {Array} breadcrumbs - 当前的面包屑路径（用于递归）
 * @returns {Array|null} - 返回完整的面包屑路径数组，未找到则返回null
 */
function findBreadcrumbs(menus, routePath, breadcrumbs = []) {
  if (!menus || !Array.isArray(menus)) {
    return null;
  }

  // 保护：routePath 必须是字符串
  if (!routePath || typeof routePath !== 'string') {
    console.warn('findBreadcrumbs: routePath 无效', routePath);
    return null;
  }

  for (let menu of menus) {
    // 如果当前菜单的Path匹配路由路径(去除前导斜杠以适配不同格式)
    const menuPath = menu.Path ? menu.Path.replace(/^\//, '') : '';
    const routePathNormalized = routePath.replace(/^\//, '');

    if (menuPath === routePathNormalized) {
      // 构建当前菜单的面包屑信息
      const crumb = {
        name: menu.Name,
        icon: menu.Icon || '',
        path: menu.Path,
      };
      return [...breadcrumbs, crumb];
    }

    // 如果有子菜单，继续递归搜索
    if (menu.children && Array.isArray(menu.children) && menu.children.length > 0) {
      // 构建当前菜单的面包屑信息（仅用于作为父级传递）
      const crumb = {
        name: menu.Name,
        icon: menu.Icon || '',
        path: menu.Path,
      };
      const result = findBreadcrumbs(menu.children, routePath, [...breadcrumbs, crumb]);
      if (result) {
        return result;
      }
    }
  }

  return null;
}

/**
 * 从localStorage获取菜单列表
 * @returns {Array} - 返回菜单数组，如果获取失败返回空数组
 */
function getMenuListFromStorage() {
  try {
    const menuListStr = window.localStorage.getItem('menuList');
    if (menuListStr) {
      return JSON.parse(menuListStr);
    }
  } catch (error) {
    console.error('Error parsing menuList from localStorage:', error);
  }
  return [];
}

/**
 * 根据路由名称获取面包屑数据
 * @param {String} routeName - 路由的name属性（用来匹配菜单的Path）
 * @returns {Array} - 返回面包屑数组，每个项包含 {name, icon, path}
 */
export function getBreadcrumbsByRouteName(routeName) {
  // 保护：routeName 无效时返回默认面包屑
  if (!routeName || typeof routeName !== 'string') {
    console.warn('getBreadcrumbsByRouteName: routeName 无效', routeName);
    return [{ name: '首頁', icon: 'home', path: 'home' }];
  }

  // 特殊处理首页
  if (routeName === 'home') {
    return [{ name: '首頁', icon: 'home', path: 'home' }];
  }

  const menuList = getMenuListFromStorage();

  if (!menuList || menuList.length === 0) {
    return [{ name: '首頁', icon: 'home', path: 'home' }];
  }

  const breadcrumbs = findBreadcrumbs(menuList, routeName);

  // 如果没有找到，返回只有首页的面包屑
  if (!breadcrumbs) {
    return [{ name: '首頁', icon: 'home', path: 'home' }];
  }

  // 在最前面添加首页（首页不出现在menuList中，因为大多数时候路由会直接导航到首页）
  return [
    { name: '首頁', icon: 'home', path: 'home' },
    ...breadcrumbs,
  ];
}

/**
 * 根据菜单ID查找面包屑
 * @param {String} menuId - 菜单的ID
 * @returns {Array} - 返回面包屑数组
 */
function findBreadcrumbsById(menus, menuId, breadcrumbs = []) {
  if (!menus || !Array.isArray(menus)) {
    return null;
  }

  for (let menu of menus) {
    if (String(menu.ID) === String(menuId)) {
      const crumb = {
        name: menu.Name,
        icon: menu.Icon || '',
        path: menu.Path,
      };
      return [...breadcrumbs, crumb];
    }

    if (menu.children && Array.isArray(menu.children) && menu.children.length > 0) {
      const crumb = {
        name: menu.Name,
        icon: menu.Icon || '',
        path: menu.Path,
      };
      const result = findBreadcrumbsById(menu.children, menuId, [...breadcrumbs, crumb]);
      if (result) {
        return result;
      }
    }
  }

  return null;
}

/**
 * 根据菜单ID获取面包屑数据
 * @param {String} menuId - 菜单的ID
 * @returns {Array} - 返回面包屑数组
 */
export function getBreadcrumbsById(menuId) {
  const menuList = getMenuListFromStorage();

  if (!menuList || menuList.length === 0) {
    return [{ name: '首頁', icon: 'home', path: 'home' }];
  }

  const breadcrumbs = findBreadcrumbsById(menuList, menuId);

  if (!breadcrumbs) {
    return [{ name: '首頁', icon: 'home', path: 'home' }];
  }

  return [
    { name: '首頁', icon: 'home', path: 'home' },
    ...breadcrumbs,
  ];
}

export default {
  getBreadcrumbsByRouteName,
  getBreadcrumbsById,
  getMenuListFromStorage,
};
